/* RL Landing — Klaviyo list subscription.
   Vanilla, no module. Prefixed globals only.

   Posts to Klaviyo's client subscription endpoint, which is designed for
   browser use: it authenticates with the PUBLIC key (company_id) and can
   only add a profile to a list. No private key ever reaches the theme.

   The form is a real <form> with a real submit button, so it stays usable
   without JS — the markup falls back to a Shopify customer form. This
   script only intercepts the submit to keep the visitor on the page.

   Endpoint contract (revision 2026-07-15):
     POST https://a.klaviyo.com/client/subscriptions?company_id=<public key>
     Content-Type: application/vnd.api+json
     list id goes in relationships.list.data.id — NOT in attributes. */
(function () {
  'use strict';

  var RL_CAPTURE_SELECTOR = '[data-rl-capture]';
  var RL_CAPTURE_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  var RL_CAPTURE_REVISION = '2026-07-15';

  /* Deliberately loose: the server is the authority on deliverability.
     A stricter regex mostly rejects valid addresses (apostrophes, new
     TLDs, sub-addressing) and turns a sale away at the last step. */
  var RL_CAPTURE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* French order: day, month, year. Separators are accepted loosely so
     "7/2/1983" and "07 02 1983" both pass; the parts are validated
     numerically below rather than by the pattern. */
  var RL_CAPTURE_BIRTHDAY_RE = /^(\d{1,2})[\s./-](\d{1,2})[\s./-](\d{4})$/;

  /* Klaviyo matches birthday flows on an ISO date, so the French input
     is converted before sending. Sending it as typed would store an
     unparseable string and silently break every anniversary trigger. */
  function RL_CAPTURE_toIsoDate(value) {
    var match = RL_CAPTURE_BIRTHDAY_RE.exec(value);
    if (!match) return null;

    var day = parseInt(match[1], 10);
    var month = parseInt(match[2], 10);
    var year = parseInt(match[3], 10);

    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;
    if (year < 1900) return null;

    /* Round-trip through Date to reject impossible days such as 31/02:
       the constructor rolls them over to the next month. */
    var probe = new Date(year, month - 1, day);
    if (
      probe.getFullYear() !== year ||
      probe.getMonth() !== month - 1 ||
      probe.getDate() !== day
    ) {
      return null;
    }

    var pad = function (n) {
      return n < 10 ? '0' + n : String(n);
    };

    return year + '-' + pad(month) + '-' + pad(day);
  }

  /* Fire a Meta "Lead" conversion on a confirmed signup. Guarded on every
     hop: the pixel is loaded by an app, not the theme, so window.fbq may
     be missing (ad blocker, sandboxed Web Pixel, script still loading).
     A missing pixel must never throw and break the success path. */
  function RL_CAPTURE_trackLead(root) {
    try {
      if (typeof window.fbq !== 'function') return;

      var source = root.getAttribute('data-rl-capture-source');
      window.fbq('track', 'Lead', source ? { content_name: source } : {});
    } catch (error) {
      /* Tracking is best-effort; the subscription already succeeded. */
    }
  }

  function RL_CAPTURE_setState(root, state, message) {
    root.setAttribute('data-rl-capture-state', state);

    var status = root.querySelector('[data-rl-capture-status]');
    if (!status) return;

    status.textContent = message || '';
    /* Errors must interrupt a screen reader; a success note can wait for
       the current utterance to finish. */
    status.setAttribute('aria-live', state === 'error' ? 'assertive' : 'polite');
  }

  function RL_CAPTURE_field(root, name) {
    return root.querySelector('[data-rl-capture-field="' + name + '"]');
  }

  function RL_CAPTURE_value(root, name) {
    var field = RL_CAPTURE_field(root, name);
    return field ? field.value.trim() : '';
  }

  function RL_CAPTURE_buildPayload(root, email, firstName, birthdayIso) {
    var properties = {};

    /* Stored ISO under a merchant-controlled property name, so Klaviyo
       can treat it as a date rather than an opaque string. */
    if (birthdayIso) {
      properties[root.getAttribute('data-rl-capture-birthday-key') || 'Anniversaire'] = birthdayIso;
    }

    var profileAttributes = {
      email: email,
      subscriptions: {
        email: {
          marketing: { consent: 'SUBSCRIBED' }
        }
      }
    };

    if (firstName) profileAttributes.first_name = firstName;
    if (birthdayIso) profileAttributes.properties = properties;

    var attributes = {
      profile: {
        data: {
          type: 'profile',
          attributes: profileAttributes
        }
      }
    };

    var source = root.getAttribute('data-rl-capture-source');
    if (source) attributes.custom_source = source;

    return {
      data: {
        type: 'subscription',
        attributes: attributes,
        relationships: {
          list: {
            data: {
              type: 'list',
              id: root.getAttribute('data-rl-capture-list')
            }
          }
        }
      }
    };
  }

  function RL_CAPTURE_submit(root, event) {
    event.preventDefault();

    if (root.getAttribute('data-rl-capture-state') === 'loading') return;

    var email = RL_CAPTURE_value(root, 'email');
    var firstName = RL_CAPTURE_value(root, 'name');
    var birthday = RL_CAPTURE_value(root, 'birthday');

    if (!RL_CAPTURE_EMAIL_RE.test(email)) {
      RL_CAPTURE_setState(root, 'error', root.getAttribute('data-rl-capture-msg-invalid'));
      var emailField = RL_CAPTURE_field(root, 'email');
      if (emailField) emailField.focus();
      return;
    }

    /* The birthday stays optional; it is only validated once filled, so
       a malformed date never reaches Klaviyo as an unusable string. */
    var birthdayIso = null;
    if (birthday) {
      birthdayIso = RL_CAPTURE_toIsoDate(birthday);
      if (!birthdayIso) {
        RL_CAPTURE_setState(
          root,
          'error',
          root.getAttribute('data-rl-capture-msg-invalid-birthday')
        );
        var birthdayField = RL_CAPTURE_field(root, 'birthday');
        if (birthdayField) birthdayField.focus();
        return;
      }
    }

    var companyId = root.getAttribute('data-rl-capture-company');
    var listId = root.getAttribute('data-rl-capture-list');

    if (!companyId || !listId) {
      RL_CAPTURE_setState(root, 'error', root.getAttribute('data-rl-capture-msg-error'));
      return;
    }

    RL_CAPTURE_setState(root, 'loading', root.getAttribute('data-rl-capture-msg-loading'));

    var url = RL_CAPTURE_ENDPOINT + '?company_id=' + encodeURIComponent(companyId);

    window
      .fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: RL_CAPTURE_REVISION
        },
        body: JSON.stringify(RL_CAPTURE_buildPayload(root, email, firstName, birthdayIso))
      })
      .then(function (response) {
        /* Klaviyo answers 202 with an empty body on success. Anything
           else is a failure we should not dress up as a subscription. */
        if (response.status === 202 || response.ok) {
          RL_CAPTURE_setState(root, 'done', root.getAttribute('data-rl-capture-msg-done'));
          RL_CAPTURE_trackLead(root);
          var form = root.querySelector('[data-rl-capture-form]');
          if (form) form.hidden = true;
          return;
        }
        RL_CAPTURE_setState(root, 'error', root.getAttribute('data-rl-capture-msg-error'));
      })
      .catch(function () {
        RL_CAPTURE_setState(root, 'error', root.getAttribute('data-rl-capture-msg-error'));
      });
  }

  function RL_CAPTURE_start(root) {
    var form = root.querySelector('[data-rl-capture-form]');
    if (!form) return;

    /* Only take over once the script is live, so a failed script load
       leaves the native form behaviour intact. */
    form.setAttribute('novalidate', 'novalidate');
    form.addEventListener('submit', function (event) {
      RL_CAPTURE_submit(root, event);
    });
  }

  /* Any link already pointing at the signup anchor opens the modal
     instead of scrolling. Nothing has to change in the hero or the
     pricing section, and with the modal disabled — or the script
     absent — those links keep their native anchor behaviour. */
  function RL_CAPTURE_wireModal() {
    var modal = document.querySelector('dialog.rl-capture-modal');
    if (!modal || typeof modal.showModal !== 'function') return;

    /* init() also runs on shopify:section:load, which would stack a new
       document listener on every editor re-render. */
    if (modal.hasAttribute('data-rl-capture-wired')) return;
    modal.setAttribute('data-rl-capture-wired', '');

    /* Prefer the anchor declared on the modal, so links keep resolving
       even when the inline section is hidden. Fall back to the inline
       section's id, then to 'lettre'. */
    var section = document.querySelector('[data-rl-capture]:not(dialog)');
    var anchor =
      modal.getAttribute('data-rl-capture-anchor') ||
      (section && section.id ? section.id : 'lettre');

    document.addEventListener('click', function (event) {
      var link = event.target.closest('a[href$="#' + anchor + '"]');
      if (!link || modal.open) return;

      event.preventDefault();
      modal.showModal();

      var first = modal.querySelector('input');
      if (first) first.focus();
    });

    modal.addEventListener('click', function (event) {
      /* Clicks land on the dialog itself only when they hit the
         backdrop; anything inside stops at the panel. */
      if (event.target === modal) modal.close();
      if (event.target.closest('[data-rl-capture-close]')) modal.close();
    });
  }

  function RL_CAPTURE_init() {
    var roots = document.querySelectorAll(RL_CAPTURE_SELECTOR);
    for (var i = 0; i < roots.length; i++) RL_CAPTURE_start(roots[i]);
    RL_CAPTURE_wireModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', RL_CAPTURE_init);
  } else {
    RL_CAPTURE_init();
  }

  document.addEventListener('shopify:section:load', RL_CAPTURE_init);
})();
